import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { OPS_KEY } from '../api';
import type { BoardCampaign, CampaignValues } from './campaignValues';

/**
 * ויטרינה (PRD 5.9.3).
 *
 * ההנהלה שולחת תמונת יעד והוראות, הסניף מעלה צילום של מה שנעשה,
 * וההנהלה מאשרת או מחזירה לתיקון עם הערה.
 */

export type DisplayCampaign = {
  campaign_id: string;
  title: string;
  instructions: string | null;
  reference_image_path: string | null;
  due_date: string | null;
  submission: {
    id: string;
    photo_path: string;
    status: 'submitted' | 'approved' | 'needs_fix';
    feedback: string | null;
    created_at: string;
  } | null;
};

export type Submission = {
  id: string;
  campaign_id: string;
  branch_id: string;
  photo_path: string;
  status: 'submitted' | 'approved' | 'needs_fix';
  feedback: string | null;
  created_at: string;
  branches: { name: string } | null;
  employees: { first_name: string; last_name: string } | null;
};

const DISPLAY_KEY = [...OPS_KEY, 'display'] as const;

export function useMyCampaigns() {
  return useQuery({
    queryKey: DISPLAY_KEY,
    queryFn: async (): Promise<DisplayCampaign[]> => {
      const { data, error } = await supabase.rpc('my_display_campaigns');
      if (error) throw error;
      return (data as DisplayCampaign[] | null) ?? [];
    },
  });
}

/**
 * לוח הניהול: סידורים, הסניפים שלהם וספירת ההגשות (מיגרציה 066).
 *
 * הספירה מגיעה מהשרת. בלי זה היה צריך למשוך את כל ההגשות של כל
 * סידור רק כדי לדעת כמה סניפים עוד לא הגישו.
 */
export function useAdminCampaigns() {
  return useQuery({
    queryKey: [...DISPLAY_KEY, 'admin'],
    queryFn: async (): Promise<BoardCampaign[]> => {
      const { data, error } = await supabase.rpc('display_board');
      if (error) throw error;
      return (data as unknown as BoardCampaign[]) ?? [];
    },
  });
}

/** פריט בתור האישורים (מיגרציה 067). */
export type QueueItem = {
  id: string;
  campaign_id: string;
  campaign_title: string;
  campaign_due_date: string | null;
  reference_image_path: string | null;
  instructions: string | null;
  branch_id: string;
  branch_name: string;
  branch_color: string | null;
  photo_path: string;
  created_at: string;
  submitted_by: string | null;
};

/**
 * כל ההגשות שממתינות, מכל הסידורים.
 *
 * זו השאלה היומית — "מה מחכה לי עכשיו" — והיא שונה מהשאלה
 * "מה מצב הקמפיין" שעליה עונה הרשימה לפי סידור.
 */
export function useReviewQueue() {
  return useQuery({
    queryKey: [...DISPLAY_KEY, 'queue'],
    queryFn: async (): Promise<QueueItem[]> => {
      const { data, error } = await supabase.rpc('display_review_queue');
      if (error) throw error;
      return (data as unknown as QueueItem[]) ?? [];
    },
  });
}

export function useSubmissions(campaignId: string | null) {
  return useQuery({
    queryKey: [...DISPLAY_KEY, 'submissions', campaignId],
    enabled: Boolean(campaignId),
    queryFn: async (): Promise<Submission[]> => {
      const { data, error } = await supabase
        .from('display_submissions')
        .select(
          `id, campaign_id, branch_id, photo_path, status, feedback, created_at,
           branches ( name ),
           employees!display_submissions_submitted_by_fkey ( first_name, last_name )`,
        )
        .eq('campaign_id', campaignId as string)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as Submission[];
    },
  });
}

export function useDisplayMutations() {
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: DISPLAY_KEY });
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  const submit = useMutation({
    mutationFn: async (input: {
      campaign_id: string;
      branch_id: string;
      submitted_by: string;
      photo_path: string;
    }) => {
      const { error } = await supabase.from('display_submissions').insert(input);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const review = useMutation({
    mutationFn: async (input: { id: string; approve: boolean; feedback?: string }) => {
      const { error } = await supabase.rpc('review_display', {
        p_submission: input.id,
        p_approve: input.approve,
        p_feedback: input.feedback ?? undefined,
      });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  /** יצירה ועריכה באותה קריאה, כולל הסניפים (מיגרציה 066). */
  const saveCampaign = useMutation({
    mutationFn: async (values: CampaignValues) => {
      const { error } = await supabase.rpc('save_display_campaign', {
        p_id: values.id ?? undefined,
        p_title: values.title.trim(),
        p_instructions: values.instructions.trim() || undefined,
        p_reference_image_path: values.referenceImagePath ?? undefined,
        p_due_date: values.dueDate || undefined,
        p_branches: values.branchIds,
      });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const removeCampaign = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('display_campaigns').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  return { submit, review, saveCampaign, removeCampaign };
}

export const displayStatusLabel: Record<Submission['status'], string> = {
  submitted: 'ממתינה לאישור',
  approved: 'אושרה',
  needs_fix: 'חזרה לתיקון',
};

export const displayStatusTone: Record<Submission['status'], 'good' | 'warn' | 'bad'> = {
  submitted: 'warn',
  approved: 'good',
  needs_fix: 'bad',
};
